import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { format, formatDistanceToNow } from 'date-fns'
import {
  Phone, Mail, Calendar, FileText, CheckSquare, Clock, User,
  ChevronDown, ChevronUp
} from 'lucide-react'
import { activityApi } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { Activity, ActivityType, CRMEntityType } from '@/types'
import { useState } from 'react'

interface ActivityTimelineProps {
  entityType: CRMEntityType
  entityId: string
  showHeader?: boolean
  maxItems?: number
  className?: string
  embedded?: boolean // When true, renders without Card wrapper
}

const activityIcons: Record<ActivityType, React.ElementType> = {
  CALL: Phone,
  EMAIL: Mail,
  MEETING: Calendar,
  NOTE: FileText,
  TASK: CheckSquare,
}

const activityColors: Record<ActivityType, string> = {
  CALL: 'bg-green-100 text-green-700 border-green-200',
  EMAIL: 'bg-blue-100 text-blue-700 border-blue-200',
  MEETING: 'bg-purple-100 text-purple-700 border-purple-200',
  NOTE: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  TASK: 'bg-orange-100 text-orange-700 border-orange-200',
}

export function ActivityTimeline({
  entityType,
  entityId,
  showHeader = true,
  maxItems,
  className,
  embedded = false,
}: ActivityTimelineProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const { data: timeline, isLoading, error } = useQuery({
    queryKey: ['timeline', entityType, entityId],
    queryFn: () => activityApi.getTimeline(entityType, entityId),
    enabled: !!entityType && !!entityId,
  })

  if (isLoading) {
    return (
      <Card className={className}>
        {showHeader && (
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {t('crm.activityTimeline', 'Activity Timeline')}
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('messages.loadError', 'Failed to load data')}
        </CardContent>
      </Card>
    )
  }

  const activities = timeline?.activities || []
  const displayActivities = maxItems && !expanded
    ? activities.slice(0, maxItems)
    : activities

  const timelineContent = (
    <>
      {activities.length === 0 ? (
        <div className="py-6 text-center text-muted-foreground text-sm">
          {t('crm.noActivities', 'No activities yet')}
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />

          <div className="space-y-6">
            {displayActivities.map((activity) => (
              <ActivityTimelineItem key={activity.activity_id} activity={activity} />
            ))}
          </div>

          {maxItems && activities.length > maxItems && (
            <Button
              variant="ghost"
              className="w-full mt-4"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-2" />
                  {t('common.showLess', 'Show Less')}
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-2" />
                  {t('common.showMore', 'Show {{count}} More', { count: activities.length - maxItems })}
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </>
  )

  // When embedded, render without Card wrapper
  if (embedded) {
    return <div className={className}>{timelineContent}</div>
  }

  return (
    <Card className={className}>
      {showHeader && (
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {t('crm.activityTimeline', 'Activity Timeline')}
            {activities.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activities.length}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
      )}
      <CardContent>
        {timelineContent}
      </CardContent>
    </Card>
  )
}

interface ActivityTimelineItemProps {
  activity: Activity
}

function ActivityTimelineItem({ activity }: ActivityTimelineItemProps) {
  const { t } = useTranslation()
  const Icon = activityIcons[activity.activity_type]
  const colorClass = activityColors[activity.activity_type]

  return (
    <div className="relative flex gap-4 pl-2">
      {/* Icon */}
      <div className={cn(
        'relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2',
        colorClass
      )}>
        <Icon className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-medium text-sm">{activity.subject}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
              <span>{format(new Date(activity.activity_date), 'MMM d, yyyy h:mm a')}</span>
              <span>•</span>
              <span>{formatDistanceToNow(new Date(activity.activity_date), { addSuffix: true })}</span>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            {activity.activity_type}
          </Badge>
        </div>

        {activity.description && (
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
            {activity.description}
          </p>
        )}

        {/* Activity-specific details */}
        <div className="flex flex-wrap gap-2 mt-2">
          {activity.duration_minutes && (
            <Badge variant="secondary" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {activity.duration_minutes} min
            </Badge>
          )}
          {activity.assigned_to_name && (
            <Badge variant="secondary" className="text-xs">
              <User className="h-3 w-3 mr-1" />
              {activity.assigned_to_name}
            </Badge>
          )}
          {activity.activity_type === 'TASK' && activity.is_completed && (
            <Badge variant="default" className="text-xs bg-green-600">
              {t('crm.completed', 'Completed')}
            </Badge>
          )}
          {activity.activity_type === 'CALL' && activity.call_direction && (
            <Badge variant="secondary" className="text-xs">
              {activity.call_direction === 'INBOUND' ? '📞 Inbound' : '📱 Outbound'}
            </Badge>
          )}
          {activity.activity_type === 'EMAIL' && activity.email_direction && (
            <Badge variant="secondary" className="text-xs">
              {activity.email_direction === 'SENT' ? '📤 Sent' : '📥 Received'}
            </Badge>
          )}
          {activity.activity_type === 'MEETING' && activity.location && (
            <Badge variant="secondary" className="text-xs">
              📍 {activity.location}
            </Badge>
          )}
        </div>
      </div>
    </div>
  )
}

export default ActivityTimeline

